import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/lib/site";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const closeMenu = () => setOpen(false);

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 bg-white ${
          scrolled ? "border-b border-[#ebebeb]" : "border-b border-[#f7f7f7]"
        }`}
      >
        <div className="mx-auto max-w-[1200px] px-4 sm:px-6 h-[64px] sm:h-[72px] flex items-center justify-between">
          <a
            href="#top"
            data-testid="nav-logo-link"
            className="flex items-center gap-2.5 sm:gap-3 text-[#222222] no-underline focus:outline-none"
            onClick={closeMenu}
          >
            <img
              src={BRAND.logo}
              alt="Innovate IT Solutions"
              className="h-7 w-7 sm:h-8 sm:w-8 object-contain rounded-[4px]"
            />
            <span className="font-medium text-[18px] sm:text-[20px] tracking-tight text-[#222222]">
              Innovate<span className="text-[#6a6a6a]">.it</span>
            </span>
          </a>

          {/* Desktop Navigation Links */}
          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="text-[15px] font-medium text-[#222222] hover:text-[#6a6a6a] transition-colors rounded-[4px] px-1 py-0.5"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2.5 sm:gap-3">
            <a
              href="#contact"
              data-testid="nav-cta"
              className="hidden sm:inline-flex items-center justify-center rounded-[8px] bg-[#222222] px-4 sm:px-5 py-2 sm:py-2.5 text-[14px] sm:text-[15px] font-medium text-white hover:opacity-90 transition-opacity"
            >
              Start a project
            </a>

            {/* Mobile hamburger */}
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-[8px] border border-[#ebebeb] text-[#222222] hover:bg-[#f7f7f7] transition-colors"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 md:hidden bg-white pt-[72px] px-6 pb-8 flex flex-col justify-between"
          >
            <nav className="flex flex-col divide-y divide-[#ebebeb] pt-2">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={closeMenu}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="text-[18px] font-medium text-[#222222] py-3.5 no-underline"
                >
                  {l.label}
                </a>
              ))}
            </nav>

            <div className="space-y-4 pt-6 border-t border-[#ebebeb]">
              <a
                href="#contact"
                onClick={closeMenu}
                className="w-full inline-flex items-center justify-center rounded-[8px] bg-[#222222] text-white py-3 text-[15px] font-medium"
              >
                Start a project
              </a>
              <div className="text-[13px] text-[#6a6a6a] space-y-1">
                <p>{BRAND.contact.email}</p>
                <p>{BRAND.contact.phone}</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
